import type {
  OrderComparatorSummary,
  OrderStatusResponse,
  WorkspaceSearchQuery,
  WorkspaceSortColumn,
} from '@/utils/DatabaseOperations'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getWorkspaceStatusDisplay, isTestingComparatorRow } from './workspaceOrderPresentation'

export type { WorkspaceSortColumn } from '@/utils/DatabaseOperations'

export const WORKSPACE_PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 200

export type WorkspaceSort = { column: WorkspaceSortColumn; dir: 'asc' | 'desc' }

const DEFAULT_SORT: WorkspaceSort = { column: 'orderDate', dir: 'desc' }

/**
 * A proband order plus its comparators, treated as one indivisible unit for
 * display. Comparators never detach from their parent.
 */
export type WorkspaceOrderGroup = {
  order: OrderStatusResponse
  comparators: OrderComparatorSummary[]
  /**
   * Guaranteed-unique per-group key for React `key`s and the comparator expand-state map.
   * Two orders with an empty `order_id` would otherwise collide on both, so expanding one would
   * expand both and duplicate React keys would be emitted. For the normal non-empty `order_id`
   * case this equals `order_id`, so behavior is unchanged. Non-PHI (order_id / ext id / index only).
   */
  groupKey: string
}

/** A selectable status filter option, keyed by the DISPLAYED clinician label. */
export type WorkspaceStatusOption = { label: string; count: number }

export function toWorkspaceOrderGroups(orders: OrderStatusResponse[]): WorkspaceOrderGroup[] {
  return orders.map((order, i) => ({
    order,
    // Only sequencing (testing) comparators appear in the Comparators column.
    // Contact-only guardians with no lab tests are excluded from the group.
    comparators: (order.comparators ?? []).filter(isTestingComparatorRow),
    groupKey: order.order_id?.trim() || order.inflection_external_id?.trim() || `idx-${i}`,
  }))
}

/** Displayed clinician label for a raw status ('' and unmapped values -> 'Unknown'). */
function statusLabel(rawStatus: string): string {
  return getWorkspaceStatusDisplay(rawStatus).label
}

/**
 * Selectable status options from the server's per-raw-status counts (search-filtered, status
 * filter not applied), collapsed by displayed label. Selected-but-absent labels are unioned in
 * with count 0 so a vanished selection stays visible/uncheckable. Sorted case-insensitively.
 */
export function deriveStatusOptions(
  statusCounts: Record<string, number>,
  selected: ReadonlySet<string>
): WorkspaceStatusOption[] {
  const counts = new Map<string, number>()
  for (const [raw, count] of Object.entries(statusCounts)) {
    const label = statusLabel(raw)
    counts.set(label, (counts.get(label) ?? 0) + count)
  }
  for (const label of selected) {
    if (!counts.has(label)) counts.set(label, 0)
  }
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
}

export type UseWorkspaceFiltersResult = {
  search: string
  setSearch: (value: string) => void
  sort: WorkspaceSort | null
  setSort: (sort: WorkspaceSort | null) => void
  toggleSort: (column: WorkspaceSortColumn) => void
  statusFilter: ReadonlySet<string>
  toggleStatus: (label: string) => void
  clearStatusFilter: () => void
  selectAllStatuses: () => void
  deselectAllStatuses: () => void
  statusOptions: WorkspaceStatusOption[]
  allStatusesSelected: boolean
  hasActiveFilters: boolean
  ghostStatusCount: number
  page: number
  setPage: (updater: number | ((prev: number) => number)) => void
  totalGroups: number
  pageCount: number
  rangeStart: number
  rangeEnd: number
  /** Server query for the current UI state; `null` when every status is deselected (nothing to show). */
  query: WorkspaceSearchQuery | null
}

/**
 * Owns Workspace search/sort/status/page UI state and turns it into the server query. Search,
 * filtering, sorting and paging all run server-side; `statusCounts` and `total` come from the
 * latest response. Session-only: state lives on the mounted component instance and survives
 * refetches (the owner is re-rendered, not re-keyed/remounted).
 */
export function useWorkspaceFilters(
  statusCounts: Record<string, number>,
  total: number,
  /** The search the server applied to produce `statusCounts` ('' = unsearched). */
  countsSearch = ''
): UseWorkspaceFiltersResult {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Default to submission-date (created_at) newest-first so an edit (which bumps updated_at)
  // never floats an order to the top — resolves the Rady "editing floats to top" report.
  const [sort, setSort] = useState<WorkspaceSort | null>(DEFAULT_SORT)
  // Status filter model: the selection is DERIVED (all present labels minus the ones the user has
  // explicitly turned off). Persisting only deselections keeps the default "everything selected"
  // and auto-includes any status that appears in a later response instead of silently hiding it.
  const [deselectedStatuses, setDeselectedStatuses] = useState<ReadonlySet<string>>(() => new Set())
  const [page, setPage] = useState(1)

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [search])

  const toggleSort = useCallback((column: WorkspaceSortColumn) => {
    // Header click: same column flips direction; a different column starts ascending.
    setSort((prev) =>
      prev?.column === column ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { column, dir: 'asc' }
    )
  }, [])

  const toggleStatus = useCallback((label: string) => {
    setDeselectedStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
    setPage(1) // user changed the filter → back to the first page
  }, [])

  const clearStatusFilter = useCallback(() => {
    // "Clear" resets back to the default all-selected state (no deselections), not "hide everything".
    setDeselectedStatuses(new Set())
    setPage(1)
  }, [])

  // "Select all" is semantically identical to Clear: no deselections → every present status shown.
  const selectAllStatuses = clearStatusFilter

  // JSON-encoded sorted arrays: stable keys that also preserve an empty raw status ("Unknown").
  const responseRawStatuses = useMemo(() => JSON.stringify(Object.keys(statusCounts).sort()), [statusCounts])

  // Raw statuses from the last unsearched response. Under a search, counts cover only the matches,
  // so a selected status with zero matches must still come from here to stay selected (ghost notice)
  // and to keep being requested.
  const [unsearchedRawStatuses, setUnsearchedRawStatuses] = useState('[]')
  const countsSearched = countsSearch !== ''
  useEffect(() => {
    if (!countsSearched) setUnsearchedRawStatuses(responseRawStatuses)
  }, [countsSearched, responseRawStatuses])

  const knownRawStatuses = useMemo(() => {
    const raws = new Set<string>(JSON.parse(responseRawStatuses))
    if (countsSearched) (JSON.parse(unsearchedRawStatuses) as string[]).forEach((raw) => raws.add(raw))
    return [...raws].sort()
  }, [countsSearched, responseRawStatuses, unsearchedRawStatuses])

  const responseStatusLabels = useMemo(() => new Set(Object.keys(statusCounts).map(statusLabel)), [statusCounts])
  const presentStatusLabels = useMemo(() => new Set(knownRawStatuses.map(statusLabel)), [knownRawStatuses])

  const deselectAllStatuses = useCallback(() => {
    setDeselectedStatuses(new Set(presentStatusLabels))
    setPage(1)
  }, [presentStatusLabels])

  // Prune deselections for labels no longer present at all, so a delegation/org switch that removed
  // a label cannot leave the filter reading "filtered" forever. Only on unsearched counts: searched
  // counts cover just the matches, and a still-existing label must keep its deselection.
  useEffect(() => {
    if (countsSearched) return
    setDeselectedStatuses((prev) => {
      if (prev.size === 0) return prev
      const next = new Set([...prev].filter((label) => responseStatusLabels.has(label)))
      return next.size === prev.size ? prev : next
    })
  }, [responseStatusLabels, countsSearched])

  const statusFilter = useMemo<ReadonlySet<string>>(
    () => new Set([...presentStatusLabels].filter((label) => !deselectedStatuses.has(label))),
    [presentStatusLabels, deselectedStatuses]
  )

  const allStatusesSelected = useMemo(
    () => [...presentStatusLabels].every((label) => !deselectedStatuses.has(label)),
    [presentStatusLabels, deselectedStatuses]
  )

  const statusOptions = useMemo(() => deriveStatusOptions(statusCounts, statusFilter), [statusCounts, statusFilter])

  // Selected labels that currently match zero rows (post-search) — drives the ghost notice.
  const ghostStatusCount = useMemo(
    () => (allStatusesSelected ? 0 : statusOptions.filter((o) => statusFilter.has(o.label) && o.count === 0).length),
    [allStatusesSelected, statusOptions, statusFilter]
  )

  // Raw statuses to request. Stable string key so a new-but-equal counts object never refetches.
  const selectedRawStatuses = useMemo(
    () =>
      JSON.stringify(allStatusesSelected ? [] : knownRawStatuses.filter((raw) => statusFilter.has(statusLabel(raw)))),
    [allStatusesSelected, knownRawStatuses, statusFilter]
  )

  const query = useMemo<WorkspaceSearchQuery | null>(() => {
    const statuses: string[] = JSON.parse(selectedRawStatuses)
    // Nothing selected matches nothing; an empty `statuses` list would instead mean "no filter",
    // so short-circuit to the explicit empty state.
    if (!allStatusesSelected && statuses.length === 0) return null
    const effectiveSort = sort ?? DEFAULT_SORT
    return {
      search: debouncedSearch.trim(),
      statuses,
      sort: effectiveSort.column,
      dir: effectiveSort.dir,
      page,
      page_size: WORKSPACE_PAGE_SIZE,
    }
  }, [allStatusesSelected, selectedRawStatuses, sort, debouncedSearch, page])

  const effectiveTotal = query === null ? 0 : total
  const pageCount = Math.max(1, Math.ceil(effectiveTotal / WORKSPACE_PAGE_SIZE))

  // Reset to the first page when SEARCH or SORT changes the result set. Status-filter changes reset
  // the page directly in the toggle handlers (user intent) so the prune effect never bounces the
  // user off their current page on a plain refetch.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, sort])

  useEffect(() => {
    setPage((prev) => Math.min(prev, pageCount))
  }, [pageCount])

  const rangeStart = effectiveTotal === 0 ? 0 : (page - 1) * WORKSPACE_PAGE_SIZE + 1
  const rangeEnd = Math.min(page * WORKSPACE_PAGE_SIZE, effectiveTotal)

  return {
    search,
    setSearch,
    sort,
    setSort,
    toggleSort,
    statusFilter,
    toggleStatus,
    clearStatusFilter,
    selectAllStatuses,
    deselectAllStatuses,
    statusOptions,
    allStatusesSelected,
    hasActiveFilters: search.trim() !== '' || !allStatusesSelected,
    ghostStatusCount,
    page,
    setPage,
    totalGroups: effectiveTotal,
    pageCount,
    rangeStart,
    rangeEnd,
    query,
  }
}
